import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { IMAGES } from "../lib/brand";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Reset your password · Il Salotto Digitale"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen pt-20 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMAGES.interior} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute bottom-12 left-12 right-12 text-bg">
          <p className="text-[11px] uppercase tracking-ultra mb-4 text-bg/70">Il Salotto Digitale</p>
          <p className="font-serif text-4xl font-light leading-tight">A moment's care, and you're back in.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:py-0">
        <div className="w-full max-w-md">
          {sent ? (
            <div data-testid="forgot-password-success">
              <div className="w-14 h-14 border border-line flex items-center justify-center"><Check size={26} strokeWidth={1.4} className="text-ink" /></div>
              <h1 className="mt-8 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Check your email</h1>
              <p className="mt-5 text-sm text-ink-soft font-light leading-relaxed">
                If an account exists for that email, a password reset link is on its way. The link is valid for one hour.
              </p>
              <Link to="/login" data-testid="back-to-login" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                Return to sign in <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-ultra text-accent">Member Area</p>
              <h1 className="mt-4 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Reset your password</h1>
              <p className="mt-4 text-sm text-ink-soft font-light">Enter the email associated with your account and we'll send you a secure link to choose a new password.</p>

              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="forgot-password-form">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Email</label>
                  <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="you@email.com" />
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-submit"
                  className="group w-full bg-ink text-bg py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#33312E] transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
                  {loading ? "Sending…" : "Send Reset Link"}
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>

              <Link to="/login" className="mt-8 inline-block text-[11px] uppercase tracking-[0.2em] text-ink-soft link-underline">← Back to sign in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
